import { z } from "zod";
import { Button, Group, Modal, PasswordInput } from "@mantine/core";
import { useCallback, useMemo, type FC } from "react";
import { useTRPC } from "../../../trpc";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { FormattedMessage, useIntl } from "react-intl";
import { passwordMismatch, passwordSchemaLength, passwordSchemaStrength } from "../../../utils/schema";
import { PasswordStrength } from "../../../components/PasswordStrength";
import { useMutation } from "@tanstack/react-query";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

const initialValues = {
  oldPassword: "",
  newPassword: "",
  passwordConfirmation: "",
};

export const ChangePasswordModal: FC<ChangePasswordModalProps> = ({ open, onClose }) => {
  const intl = useIntl();
  const trpc = useTRPC();
  const mapkaNotification = useMapkaNotification();

  const { mutate: changePassword, isPending } = useMutation(trpc.changePassword.mutationOptions());

  const validate = useMemo(() => zod4Resolver(
    z.object({
      oldPassword: passwordSchemaLength(intl),
      newPassword: passwordSchemaStrength(intl),
      passwordConfirmation: passwordSchemaStrength(intl),
    }).refine(data => data.newPassword === data.passwordConfirmation, {
      message: passwordMismatch(intl),
      path: ["passwordConfirmation"],
    }),
  ), [intl]);

  const { onSubmit, getInputProps, isValid, values } = useForm({
    validateInputOnBlur: true,
    initialValues,
    validate,
  });

  const handleChangePassword = useCallback((form: typeof initialValues) => {
    changePassword(form, {
      onSuccess: () => {
        onClose();
        mapkaNotification({
          title: intl.formatMessage({
            defaultMessage: "Password changed",
            id: "notification.passwordChanged",
          }),
          message: intl.formatMessage({
            defaultMessage: "Your password has been successfully changed",
            id: "notification.passwordChangedMessage",
          }),
          variant: "success",
        });
      },
    });
  }, [changePassword, intl, onClose]);

  return (
    <Modal
      opened={open}
      size="lg"
      onClose={onClose}
      title={intl.formatMessage({
        defaultMessage: "Change password",
        id: "title.changePassword",
      })}
    >
      <form onSubmit={onSubmit(handleChangePassword)}>
        <PasswordInput
          id="current-password"
          label={intl.formatMessage({
            defaultMessage: "Old password",
            id: "label.oldPassword",
          })}
          autoComplete="current-password"
          {...getInputProps("oldPassword")}
        />
        <PasswordInput
          id="new-password"
          label={intl.formatMessage({
            defaultMessage: "New password",
            id: "label.newPassword",
          })}
          autoComplete="new-password"
          {...getInputProps("newPassword")}
        />
        <PasswordInput
          id="new-password-confirm"
          label={(
            <FormattedMessage
              id="label.passwordConfirmation"
              defaultMessage="Password confirmation"
            />
          )}
          autoComplete="new-password"
          {...getInputProps("passwordConfirmation")}
        />
        <PasswordStrength {...values} />
        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="reset" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="tertiary" type="submit" loading={isPending} disabled={!isValid()}>
            <FormattedMessage defaultMessage="Save" id="button.save" />
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
